<template>
  <div ref="editorDiv" class="content-text" :class="computedClasses" v-html="text"></div>
</template>

<script>
// Redactor doesn't do modules, have to use global var $R
import '@/vendor/redactor/redactor.min.js'
import '@/vendor/redactor/_plugins/handle/handle-alpha.js'
import '@/vendor/redactor/redactor.min.css'
import '@/vendor/redactor/_plugins/handle/handle.min.css'

const MODES = [
  '',
  'testBuilder',
  'createExperiment',
  'sharedReport',
  'sharedReportNoHtml',
  'addTest',
  'addTestAddInfo',
  'testTitle',
  'insights',
  'comments',
  'noHtml'
]

/* global $R */
export default {
  name: 'Redactor',
  props: {
    // The object we're modifying a prop of
    object: {
      type: Object,
      required: true
    },
    // The name of the field
    field: {
      type: String,
      required: true
    },
    mode: {
      type: String,
      default: () => '',
      // Whitelist of modes, good for reference and for avoiding undefined behavior
      validator: (val) => MODES.includes(val)
    },
    classes: {
      type: String,
      default: () => ''
    },
    placeholder: {
      type: String,
      default: () => ''
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    formattedUsersToTag: {
      type: Object,
      default: () => ({})
    },
    id: {
      type: String,
      required: false,
      default: ''
    },
    focusEnd: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      editor: false,
      text: this.object[this.field]
    }
  },
  computed: {
    buttons () {
      switch (this.mode) {
        case 'addTest':
        case 'testBuilder':
          return ['bold', 'italic', 'underline']
        case 'comments':
          return ['bold', 'italic', 'underline', 'link']
        default:
          return ['bold', 'italic', 'underline', 'ol', 'ul', 'indent', 'outdent', 'link']
      }
    },
    toolbar () {
      // disable toolbar for some modes ?
      switch (this.mode) {
        case 'noHtml':
        case 'sharedReportNoHtml':
        case 'testTitle':
        case 'insights':
          return false
        default:
          return true
      }
    },
    plugins () {
      switch (this.mode) {
        case 'comments':
          return ['handle']
        default:
          return false
      }
    },
    air () {
      // use Air toolbar for some modes
      switch (this.mode) {
        case 'testBuilder':
        case 'sharedReport':
        case 'addTest':
        case 'comments':
          return true
        default:
          return false
      }
    },
    computedClasses () {
      /* NOTE: Do NOT, I repeat, do NOT ever change the classes on the root redactor element after the instance has been initialized. In other words, these classes should ONLY ever vary with properties that do not themselves change. If you need conditional & dynamic classes for some reason, create them on the parent element. */
      let classes = this.classes + ' redactor-base'
      switch (this.mode) {
        case 'sharedReport':
        case 'sharedReportNoHtml':
          classes += ' click-edit'
          break
        default:
          break
      }
      return classes
    },
    styles () {
      switch (this.mode) {
        case 'sharedReport':
        case 'sharedReportNoHtml':
        case 'addTest':
        case 'testTitle':
        case 'insights':
          return false
        default:
          return true
      }
    },
    shortcuts () {
      switch (this.mode) {
        case 'noHtml':
        case 'sharedReportNoHtml':
        case 'testTitle':
        case 'createExperiment':
          return false
        default:
          return true
      }
    },
    placeholderText () {
      if (this.readOnly && this.mode !== 'addTest') {
        return ''
      } else {
        return this.placeholder
      }
    },
    pasteBlockTags () {
      // limit block tags allowed on paste
      switch (this.mode) {
        case 'noHtml':
        case 'sharedReportNoHtml':
        case 'insights':
        case 'testTitle':
          return []
        case 'testBuilder':
        case 'createExperiment':
          return ['p']
        default:
          return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'p', 'div']
      }
    },
    pasteInlineTags () {
      // limit inline block tags allowed on paste
      switch (this.mode) {
        case 'noHtml':
        case 'sharedReportNoHtml':
        case 'insights':
        case 'testTitle':
          return []
        case 'testBuilder':
          return ['br', 'strong', 'span', 'b', 'u', 'em', 'i']
        default:
          return ['a', 'br', 'strong', 'del', 'span', 'b', 'u', 'em', 'i']
      }
    },
    breakline () {
      switch (this.mode) {
        case 'testBuilder':
        case 'noHtml':
        case 'testTitle':
        case 'insights':
        case 'sharedReport':
          return true
        default:
          return false
      }
    },
    pastePlainText () {
      switch (this.mode) {
        case 'noHtml':
        case 'testTitle':
          return true
        default:
          return false
      }
    },
    config () {
      const that = this
      return {
        styles: this.styles,
        autoparseImages: false,
        autoparseLinks: false,
        shortcuts: this.shortcuts,
        breakline: this.breakline,
        buttons: this.buttons,
        focusEnd: this.focusEnd,
        plugins: this.plugins,
        handle: 'none',
        handleUsers: this.formattedUsersToTag,
        linkTarget: '_blank', // open everything in new tab
        linkNofollow: true, // prevents indexing and improves security
        pasteLinkTarget: '_blank', // as above, but for pasted content
        pasteImages: false, // for now
        grammarly: true,
        placeholder: this.placeholderText,
        toolbar: this.toolbar,
        air: this.air,
        pasteBlockTags: this.pasteBlockTags,
        pasteInlineTags: this.pasteInlineTags,
        pastePlainText: this.pastePlainText,
        removeComments: false,
        callbacks: {
          started () {
            if (that.readOnly) {
              this.enableReadOnly()
            }
          },
          synced (html) {
            // redactor editor is independent and don't need permission
            that.object[that.field] = html
            // For anything listening...we'll let them know what we've done.
            that.$emit('setContent', html)
          },
          blur (e) {
            that.$emit('blur', e)
          },
          focus (e) {
            that.$emit('focus', e)
          },
          click (e) {
            that.$emit('click', e)
          },
          keydown (e) {
            e.stopPropagation()
          },
          keyup (e) {
            e.stopPropagation()
            that.$emit('keyUp', this.source.getCode())
          }
        }
      }
    }
  },
  watch: {
    readOnly: {
      handler: function () {
        if (this.editor) {
          this.refresh()
        }
      }
    },
    formattedUsersToTag: function () {
      if (this.editor) {
        this.refresh()
      }
    }
  },
  created () {
    // https://vuejs.org/v2/api/#vm-watch
    // watch for changes from the top-level, so we can propagate them down
    this.$watch(() => {
      return this.object[this.field]
    },
    this.onPropChange)
  },
  mounted () {
    this.editor = $R(this.$refs.editorDiv, this.config) // happens synchronously
  },
  beforeDestroy () {
    $R(this.$refs.editorDiv, 'destroy')
    this.editor = null
    this.$forceUpdate()
  },
  methods: {
    // If something changes on the prop level, update the text here
    onPropChange (newVal, oldVal) {
      if (!this.editor) return
      if (newVal !== this.editor.source.getCode()) {
        // console.log('Prop update detected:', newVal, this.editor.source.getCode())
        this.text = newVal
        this.editor.source.setCode(this.text)
      }
    },
    refresh () {
      $R(this.$refs.editorDiv, 'destroy')
      this.editor = $R(this.$refs.editorDiv, this.config)
    }
  }
}
</script>

<style scoped lang='scss'>
  /* Yair: These read-only exclusions don't work entirely as expected,
  due to both minor redactor bugs and inconsistencies in the way we present inputs to users. Revisit soon. */
  .redactor-base:not(.redactor-read-only) {
    @include editableField();
    &:focus { outline: none; }
  }

  .redactor-read-only:hover,
  .redactor-read-only:focus {
    background: $grey-light !important;
    border-bottom-color: $grey-lighter !important;
  }

  .redactor-read-only:focus {
    border-bottom-style: dotted !important;
  }

  a.re-button-icon {
    padding: 6px 8px 4px 8px !important;
  }

  .redactor-styles {
    font-family: inherit;
  }

  .editable ul {
    list-style-type: disc;
  }

  .editable ol {
    list-style-type: decimal;
  }

  ::v-deep {
    .redactor-styles-on {
      border: 0 !important;
    }

    .redactor-base ul {
      margin-bottom: $spacing-sm-md;
    }
  }

  .redactor-focus.redactor-styles-on {
    border: 0 !important;
  }

</style>
